import { Helmet } from 'react-helmet-async';
import React from 'react';

interface SEOOptions {
  title: string;
  description?: string;
  canonical?: string;
  image?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  author?: string;
  keywords?: string;
  structuredData?: object;
  noindex?: boolean;
  faq?: { question: string; answer: string }[];
}

const BASE = 'FinCalc';

export const usePageSEO = ({ title, description, canonical, image, type = 'website', publishedTime, author, keywords, structuredData, noindex = false, faq }: SEOOptions) => {
  const fullTitle = title.includes(BASE) ? title : `${title} | ${BASE}`;
  
  return {
    HelmetComponent: (
      <Helmet>
        <title>{fullTitle}</title>
        {description && <meta name="description" content={description} />}
        {description && <meta property="og:description" content={description} />}
        {description && <meta name="twitter:description" content={description} />}
        
        <meta property="og:title" content={fullTitle} />
        <meta name="twitter:title" content={fullTitle} />
        
        {canonical && <link rel="canonical" href={canonical} />}
        {canonical && <meta property="og:url" content={canonical} />}
        
        <meta property="og:type" content={type} />
        
        {image && <meta property="og:image" content={image} />}
        {image && <meta property="og:image:width" content="1200" />}
        {image && <meta property="og:image:height" content="630" />}
        {image && <meta name="twitter:image" content={image} />}
        {image && <meta name="twitter:card" content="summary_large_image" />}
        
        <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'} />
        <meta name="googlebot" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
        
        {keywords && <meta name="keywords" content={keywords} />}
        
        {type === 'article' && (
          <>
            {publishedTime && <meta property="article:published_time" content={publishedTime} />}
            {author && <meta property="article:author" content={author} />}
            <meta property="article:section" content="Finance" />
          </>
        )}
        
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}

        {faq && faq.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              'mainEntity': faq.map(f => ({
                '@type': 'Question',
                'name': f.question,
                'acceptedAnswer': {
                  '@type': 'Answer',
                  'text': f.answer
                }
              }))
            })}
          </script>
        )}

      </Helmet>
    )
  };
};
